import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  X, Loader2, Zap, CheckCircle2, AlertTriangle, Clock, Circle, Layers, Ban,
  Pause, Play,
} from "lucide-react";
import { cn } from "@/lib/util";
import { usePermissions } from "@/lib/permissions";
import AssetTargetPicker from "@/components/scanners/AssetTargetPicker";

/**
 * FullScanModal
 * -------------
 * Launches the 7-phase "Escaneo Completo" preset.
 *
 * Two modes:
 *  1) Single-asset  → picks one target and polls one full_scan_run.
 *  2) All-assets    → target = "__ALL__". Backend fans out into a batch
 *     that runs one full-scan per asset, strictly sequential. UI polls
 *     the batch endpoint and shows "Asset X of N · Phase Y · Tool Z".
 */
export default function FullScanModal({ clients, onClose, onLaunched,
                                          initialRunId = null, initialBatchId = null,
                                          onScanStart, onScanComplete }) {
  const [clientId, setClientId] = useState(clients[0]?.id || "");
  const [target, setTarget] = useState("");
  const [pickerTargets, setPickerTargets] = useState([]);
  const [authorized, setAuthorized] = useState(false);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [runId, setRunId] = useState(initialRunId);
  const [batchId, setBatchId] = useState(initialBatchId);
  const [run, setRun] = useState(null);
  const [batch, setBatch] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [pollGen, setPollGen] = useState(0);
  const { isAdmin } = usePermissions();

  const client = clients.find((c) => c.id === clientId);
  const isAllAssets = target === "all";

  useEffect(() => { setTarget(""); }, [clientId]);

  useEffect(() => {
    api.get("/scans/preset/full-scan/info")
      .then((r) => setInfo(r.data))
      .catch(() => setInfo(null));
  }, []);

  // Poll — batch takes priority when in all-assets mode
  useEffect(() => {
    if (!runId && !batchId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        if (batchId) {
          const r = await api.get(`/scans/preset/full-scan/batch/${batchId}`);
          if (cancelled) return;
          setBatch(r.data);
          setRun(r.data.current_run || null);
          const terminal = r.data.status === "done"
                          || r.data.status === "error"
                          || r.data.status === "cancelled";
          if (terminal) { onScanComplete && onScanComplete(); }
          else { setTimeout(tick, 2000); }
        } else {
          const r = await api.get(`/scans/preset/full-scan/${runId}`);
          if (cancelled) return;
          setRun(r.data);
          const terminal = r.data.status === "done"
                          || r.data.status === "error"
                          || r.data.status === "cancelled";
          if (terminal) { onScanComplete && onScanComplete(); }
          else { setTimeout(tick, 2000); }
        }
      } catch {
        /* keep polling silently */
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [runId, batchId, pollGen, onScanComplete]);

  const submit = async (e) => {
    e.preventDefault();
    if (!authorized || !target || !clientId) return;
    setBusy(true);
    try {
      const r = await api.post("/scans/preset/full-scan", {
        client_id: clientId, target, authorized: true,
      });
      if (r.data.batch) {
        setBatchId(r.data.id);
        onScanStart && onScanStart(null, r.data.id);
        toast.success(`Escaneo por lotes lanzado — ${r.data.total} activos en cola`);
      } else {
        setRunId(r.data.id);
        onScanStart && onScanStart(r.data.id, null);
        toast.success("Escaneo Completo lanzado — 7 fases en marcha");
      }
      onLaunched && onLaunched();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo lanzar el preset");
    } finally {
      setBusy(false);
    }
  };

  const cancelBatch = async () => {
    if (!batchId || cancelling) return;
    setCancelling(true);
    try {
      const r = await api.post(`/scans/preset/full-scan/batch/${batchId}/cancel`);
      if (r.data?.already_terminal) {
        toast.info(`El batch ya estaba en estado ${r.data.status}`);
      } else {
        toast.success("Batch cancelado · las fases pendientes no se ejecutarán");
      }
      setConfirmCancel(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cancelar el batch");
    } finally {
      setCancelling(false);
    }
  };

  const pauseBatch = async () => {
    if (!batchId || pausing) return;
    setPausing(true);
    try {
      const r = await api.post(`/scans/preset/full-scan/batch/${batchId}/pause`);
      if (r.data?.already_paused) {
        toast.info("El batch ya estaba pausado");
      } else {
        toast.success("Batch pausado · el activo actual termina y los siguientes esperan a que reanudes");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo pausar el batch");
    } finally {
      setPausing(false);
    }
  };

  const resumeBatch = async () => {
    if (!batchId || resuming) return;
    setResuming(true);
    try {
      const r = await api.post(`/scans/preset/full-scan/batch/${batchId}/resume`);
      if (r.data?.action === "resumed_soft") {
        toast.success("Batch reanudado · el activo en curso continúa y los siguientes se encolan al terminar");
      } else if (r.data?.action === "restarted") {
        toast.success(`Batch reanudado · escaneando ${r.data.target} (${(r.data.resumed_from_index ?? 0) + 1}/${batch?.total || "?"})`);
      } else if (r.data?.action === "completed_no_pending") {
        toast.info("El batch ya estaba completo — no quedan activos por escanear");
      } else {
        toast.success("Batch reanudado");
      }
      // Re-arm floating badge in Layout (Scanners.jsx cleared it on cancel).
      onScanStart && onScanStart(null, batchId);
      // Restart the poll loop — it had exited on `cancelled` state.
      setPollGen((g) => g + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo reanudar el batch");
    } finally {
      setResuming(false);
    }
  };

  const statusIcon = (s) => {
    if (s === "running" || s === "queued")
      return <Loader2 className="w-3 h-3 animate-spin text-primary" />;
    if (s === "done") return <CheckCircle2 className="w-3 h-3 text-sev-low" />;
    if (s === "error") return <AlertTriangle className="w-3 h-3 text-sev-critical" />;
    if (s === "skipped") return <Circle className="w-3 h-3 text-muted-foreground" />;
    return <Clock className="w-3 h-3 text-muted-foreground" />;
  };

  const skippedCount = run
    ? run.phases.flatMap((p) => p.tools).filter((t) => t.status === "skipped").length
    : 0;

  // Current phase + running tool label for the batch header line
  const currentPhaseIdx = run ? (run.current_phase_idx || 0) : 0;
  const currentPhase = run?.phases?.[currentPhaseIdx];
  const runningTool = currentPhase?.tools?.find(
    (t) => t.status === "running" || t.status === "queued"
  );

  return (
    <div
      className="fixed inset-0 z-50 bg-background/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-4xl bg-card border border-border max-h-[92vh] flex flex-col"
        data-testid="full-scan-modal"
      >
        <div className="p-5 border-b border-border flex items-start justify-between gap-4">
          <div>
            <div className="overline mb-1 flex items-center gap-2 text-primary">
              <Zap className="w-3 h-3" /> Preset · Escaneo Completo
            </div>
            <h2 className="font-display text-2xl font-medium">
              7 fases secuenciales · orquestación inteligente
            </h2>
            <p className="text-xs text-muted-foreground mt-2 max-w-2xl">
              Reconocimiento → puertos → fingerprinting web → TLS/DNS → vulnerabilidades →
              threat intel → credenciales. Cada fase espera el fin de la anterior; las
              herramientas sin API key se marcan como saltadas sin abortar el flujo.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-muted"
            data-testid="full-scan-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {!runId && !batchId && (
            <form onSubmit={submit} className="p-5 space-y-4">
              {info && (
                <div className="space-y-2">
                  <div className="overline">Fases · {info.phases.length}</div>
                  {info.phases.map((ph) => (
                    <div
                      key={ph.idx}
                      className="border border-border p-3 bg-background/40"
                      data-testid={`fs-phase-preview-${ph.idx}`}
                    >
                      <div className="font-mono text-[11px] tracking-widest uppercase text-primary mb-2">
                        {ph.idx + 1} · {ph.name}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {ph.tools.map((t) => (
                          <span
                            key={t.tool_id}
                            className={cn(
                              "font-mono text-[10px] px-2 py-0.5 border",
                              t.available
                                ? "border-sev-low/40 text-sev-low bg-sev-low/5"
                                : "border-border text-muted-foreground bg-background/40"
                            )}
                            title={
                              t.available
                                ? "Disponible"
                                : `Sin ${(t.requires_env || []).join(", ") || "API key"} — se saltará`
                            }
                          >
                            {t.available ? "· " : "○ "}
                            {t.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                  <div className="overline mb-1">Cliente</div>
                  <select
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
                    data-testid="full-scan-client-select"
                  >
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <div className="overline mb-1">Objetivo (dominio, IP o todos)</div>
                  {isAllAssets ? (
                    <div
                      className="w-full bg-primary/10 border border-primary/50 px-3 py-3 flex items-center justify-between"
                      data-testid="full-scan-all-assets-summary"
                    >
                      <div className="flex items-center gap-3">
                        <Layers className="w-4 h-4 text-primary flex-shrink-0" />
                        <div>
                          <div className="font-display text-sm">
                            Se escanearán {pickerTargets.length} activo(s) secuencialmente
                          </div>
                          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">
                            uno tras otro · nunca en paralelo
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setTarget("")}
                        className="text-[10px] font-mono uppercase tracking-widest text-primary hover:underline"
                        data-testid="full-scan-all-assets-change"
                      >
                        Cambiar
                      </button>
                    </div>
                  ) : (
                    <AssetTargetPicker
                      clientId={clientId}
                      value={target}
                      onChange={(v, list) => { setTarget(v); setPickerTargets(list); }}
                      targetType="any"
                      allowAll={true}
                      testId="full-scan-target-picker"
                    />
                  )}
                </label>
              </div>

              {isAllAssets && (
                <div className="border border-primary/40 bg-primary/5 p-3 flex items-start gap-3"
                     data-testid="full-scan-all-assets-notice">
                  <Layers className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <div className="text-xs">
                    <div className="font-mono text-primary uppercase tracking-widest text-[10px] mb-1">
                      Modo por lotes
                    </div>
                    <p>
                      Se ejecutará un <b>escaneo completo de 7 fases por cada uno</b> de los{" "}
                      <b>{pickerTargets.length}</b> activos del cliente,{" "}
                      <b>uno tras otro</b> (nunca en paralelo). Cada activo tarda varios
                      minutos — el proceso puede requerir de una a varias horas.
                    </p>
                  </div>
                </div>
              )}

              {client && (
                <div className="text-[10px] font-mono text-muted-foreground">
                  Alcance autorizado: {(client.root_domains || []).join(", ") || "sin dominios"}
                  {(client.ip_ranges || []).length > 0 &&
                    ` · IPs: ${client.ip_ranges.join(", ")}`}
                </div>
              )}

              <label className="flex items-start gap-3 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={authorized}
                  onChange={(e) => setAuthorized(e.target.checked)}
                  className="mt-0.5 accent-primary"
                  data-testid="full-scan-auth-checkbox"
                />
                <span>
                  Declaro que dispongo de <b>autorización escrita</b> del cliente para
                  ejecutar un escaneo activo completo (7 fases · múltiples herramientas activas)
                  sobre{" "}
                  <code className="font-mono text-primary">
                    {isAllAssets
                      ? `todos los activos (${pickerTargets.length})`
                      : (target || "el objetivo")}
                  </code>.
                </span>
              </label>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={busy || !authorized || !target || !clientId}
                  className="bg-primary text-primary-foreground px-4 py-2 text-sm hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="full-scan-submit-btn"
                >
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                  {isAllAssets ? "Lanzar escaneo por lotes" : "Lanzar Escaneo Completo"}
                </button>
              </div>
            </form>
          )}

          {(runId || batchId) && run && (
            <div className="p-5 space-y-3" data-testid="full-scan-progress">
              {batch && (
                <div
                  className="border border-primary/40 bg-primary/5 p-3 flex items-center justify-between gap-3"
                  data-testid="full-scan-batch-header"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Layers className="w-4 h-4 text-primary flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="font-mono text-[10px] tracking-widest uppercase text-primary">
                        Batch · Activo {Math.min(batch.current_index + 1, batch.total)} de {batch.total}
                        {batch.status === "cancelled" && (
                          <span className="ml-2 text-sev-critical">· CANCELADO</span>
                        )}
                        {batch.status === "paused" && (
                          <span className="ml-2 text-sev-medium">· PAUSADO</span>
                        )}
                      </div>
                      <div className="font-display text-sm mt-0.5 truncate" data-testid="full-scan-batch-current-target">
                        {run.target}
                        {runningTool && batch.status !== "cancelled" && (
                          <span className="text-muted-foreground text-xs ml-2 font-mono">
                            · Fase {(currentPhaseIdx + 1)} · {runningTool.label} en curso
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right">
                      <div className="font-display text-xl">
                        {batch.findings_total + (run.findings_total || 0)}
                      </div>
                      <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
                        hallazgos totales
                      </div>
                    </div>
                    {batch.status !== "done"
                      && batch.status !== "cancelled"
                      && batch.status !== "error"
                      && batch.status !== "paused"
                      && !confirmCancel && (
                        <button
                          type="button"
                          onClick={() => setConfirmCancel(true)}
                          className="border border-sev-critical/50 text-sev-critical hover:bg-sev-critical/10 px-3 py-2 font-mono text-[10px] tracking-widest uppercase inline-flex items-center gap-1.5"
                          data-testid="full-scan-batch-cancel-btn"
                          title="Detiene el batch — el tool en curso termina; los siguientes activos y fases no se ejecutarán"
                        >
                          <Ban className="w-3 h-3" />
                          Cancelar batch
                        </button>
                    )}
                  </div>
                </div>
              )}

              {confirmCancel && batch && (
                <div className="border border-sev-critical/50 bg-sev-critical/5 p-4"
                     data-testid="full-scan-cancel-confirm">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-4 h-4 text-sev-critical mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-sev-critical mb-1">
                        ¿Cancelar el batch en curso?
                      </div>
                      <p className="text-xs">
                        Faltan <b>{batch.total - batch.current_index}</b> activo(s) por escanear
                        (incluyendo el actual). El tool corriendo ahora <b>terminará solo</b>
                        (los subprocesos no se matan) pero <b>ninguna fase o activo posterior</b>
                        se ejecutará. Los hallazgos ya persistidos se conservan.
                      </p>
                      <div className="flex gap-2 mt-3">
                        <button
                          type="button"
                          onClick={() => setConfirmCancel(false)}
                          className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
                          data-testid="full-scan-cancel-abort"
                        >
                          Volver
                        </button>
                        <button
                          type="button"
                          onClick={cancelBatch}
                          disabled={cancelling}
                          className="bg-sev-critical text-white px-3 py-1.5 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50 hover:bg-sev-critical/90"
                          data-testid="full-scan-cancel-confirm-btn"
                        >
                          {cancelling
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <Ban className="w-3 h-3" />}
                          Sí, cancelar batch
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {batch && (
                <div className="w-full h-1 bg-background/50" data-testid="full-scan-batch-progress-bar">
                  <div
                    className="h-1 bg-primary transition-all duration-500"
                    style={{
                      width: `${Math.min(100,
                        (batch.current_index / batch.total) * 100
                        + (run.status === "done" ? (1 / batch.total) * 100 : 0))}%`
                    }}
                  />
                </div>
              )}

              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="overline">Progreso · {run.target}</div>
                <div className="flex items-center gap-2">
                  {isAdmin && batch && batch.status === "running" && !confirmCancel && (
                    <button
                      type="button"
                      onClick={pauseBatch}
                      disabled={pausing}
                      className="border border-sev-medium/50 text-sev-medium hover:bg-sev-medium/10 px-3 py-1 font-mono text-[10px] tracking-widest uppercase inline-flex items-center gap-1.5 disabled:opacity-50"
                      data-testid="full-scan-pause-btn"
                      title="El activo actual termina normalmente; los siguientes esperan a que reanudes"
                    >
                      {pausing
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Pause className="w-3 h-3" />}
                      Pausar
                    </button>
                  )}
                  {isAdmin && batch && (batch.status === "paused" || batch.status === "cancelled") && (
                    <button
                      type="button"
                      onClick={resumeBatch}
                      disabled={resuming}
                      className="border border-primary/50 text-primary hover:bg-primary/10 px-3 py-1 font-mono text-[10px] tracking-widest uppercase inline-flex items-center gap-1.5 disabled:opacity-50"
                      data-testid="full-scan-resume-btn"
                      title={batch.status === "cancelled"
                        ? "Reinicia desde el último activo sin completar"
                        : "Continúa desde el siguiente activo pendiente"}
                    >
                      {resuming
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Play className="w-3 h-3" />}
                      Reanudar
                    </button>
                  )}
                  <span
                    className={cn(
                      "font-mono text-[10px] tracking-widest uppercase border px-2 py-0.5",
                      (batch?.status || run.status) === "done"
                        ? "text-sev-low border-sev-low/40 bg-sev-low/10"
                        : (batch?.status || run.status) === "error"
                        ? "text-sev-critical border-sev-critical/40 bg-sev-critical/10"
                        : (batch?.status || run.status) === "paused"
                        ? "text-sev-medium border-sev-medium/40 bg-sev-medium/10"
                        : (batch?.status || run.status) === "cancelled"
                        ? "text-sev-critical border-sev-critical/40 bg-sev-critical/10"
                        : "text-primary border-primary/40 bg-primary/10"
                    )}
                    data-testid="full-scan-status"
                  >
                    {batch?.status || run.status}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-center">
                <StatBadge
                  label="Fase actual"
                  value={`${(run.current_phase_idx || 0) + 1}/${run.phases.length}`}
                  tone="primary"
                />
                <StatBadge
                  label="Hallazgos"
                  value={run.findings_total || 0}
                  tone="sev-medium"
                />
                <StatBadge label="Tools saltadas" value={skippedCount} tone="muted" />
              </div>

              <div className="space-y-2">
                {run.phases.map((ph) => (
                  <div
                    key={ph.idx}
                    className={cn(
                      "border p-3",
                      ph.status === "done"
                        ? "border-sev-low/40 bg-sev-low/5"
                        : ph.status === "running"
                        ? "border-primary/50 bg-primary/5"
                        : "border-border bg-background/40"
                    )}
                    data-testid={`fs-phase-${ph.idx}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-mono text-[11px] tracking-widest uppercase flex items-center gap-2">
                        {statusIcon(ph.status)} {ph.idx + 1} · {ph.name}
                      </div>
                      <span className="font-mono text-[10px] text-muted-foreground">
                        {
                          ph.tools.filter((t) =>
                            ["done", "error", "skipped"].includes(t.status)
                          ).length
                        }
                        /{ph.tools.length}
                      </span>
                    </div>
                    <div className="space-y-1">
                      {ph.tools.map((t) => (
                        <div
                          key={t.tool_id}
                          className="flex items-center justify-between text-[11px] font-mono"
                          data-testid={`fs-tool-${t.tool_id}`}
                        >
                          <span className="flex items-center gap-2">
                            {statusIcon(t.status)}
                            <span
                              className={cn(
                                t.status === "skipped" &&
                                  "text-muted-foreground line-through"
                              )}
                            >
                              {t.label}
                            </span>
                            {t.status === "skipped" && (
                              <span className="text-[9px] text-muted-foreground italic">
                                ({t.skipped_reason || "saltada"})
                              </span>
                            )}
                          </span>
                          <span className="text-muted-foreground">
                            {t.findings_count > 0 ? (
                              <span className="text-sev-critical">
                                {t.findings_count} hallazgos
                              </span>
                            ) : t.status === "done" ? (
                              "sin hallazgos"
                            ) : t.status === "queued" || t.status === "running" ? (
                              "…"
                            ) : (
                              "—"
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {batch && batch.run_ids && batch.run_ids.length > 0 && (
                <div className="border border-border p-3 bg-background/40"
                     data-testid="full-scan-batch-assets-list">
                  <div className="overline mb-2">Progreso por activo</div>
                  <div className="space-y-1 max-h-32 overflow-auto">
                    {batch.targets.map((tgt, i) => {
                      const done = i < batch.current_index
                        || (i === batch.current_index && (batch.status === "done"));
                      const active = i === batch.current_index && batch.status !== "done";
                      return (
                        <div
                          key={i}
                          className="flex items-center gap-2 text-[11px] font-mono"
                          data-testid={`fs-batch-asset-${i}`}
                        >
                          {done
                            ? <CheckCircle2 className="w-3 h-3 text-sev-low" />
                            : active
                            ? <Loader2 className="w-3 h-3 animate-spin text-primary" />
                            : <Circle className="w-3 h-3 text-muted-foreground" />}
                          <span className={cn(
                            active && "text-primary",
                            !done && !active && "text-muted-foreground",
                          )}>
                            {i + 1}. {tgt}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
                  data-testid="full-scan-close-bottom"
                >
                  Cerrar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatBadge({ label, value, tone }) {
  const toneCls =
    {
      primary: "text-primary border-primary/40 bg-primary/10",
      "sev-medium": "text-sev-medium border-sev-medium/40 bg-sev-medium/10",
      muted: "text-muted-foreground border-border bg-background/40",
    }[tone] || "text-muted-foreground border-border bg-background/40";
  return (
    <div className={cn("border p-2", toneCls)}>
      <div className="font-display text-xl font-medium">{value}</div>
      <div className="font-mono text-[9px] tracking-widest uppercase">{label}</div>
    </div>
  );
}
